import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageIcon, Upload, Download, Trash2, Check, AlertTriangle, Images, ChevronRight, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtBytes = (n) => {
  if (!n && n !== 0) return "–";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const PRESETS = [
  { key: "original", label: "Original" },
  { key: "1920", label: "1920w" },
  { key: "1280", label: "1280w" },
  { key: "640", label: "640w" },
];

export default function AdminMediaTab() {
  const [slots, setSlots] = useState([]);
  const [activeSlot, setActiveSlot] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [customWidth, setCustomWidth] = useState("");
  const [activeDownloadItem, setActiveDownloadItem] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/media`);
      setSlots(data.slots || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const slot = slots.find((s) => s.key === activeSlot) || slots[0];

  const upload = async (file) => {
    setError(""); setSuccess(""); setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await axios.post(`${API}/media/slot/${activeSlot}/upload`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccess("Bild hochgeladen.");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  const removeItem = async (item) => {
    if (!window.confirm("Bild wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/media/item/${item.id}`, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggleVisible = async (item) => {
    try {
      await axios.put(`${API}/media/item/${item.id}`, { is_visible: item.is_visible ? 0 : 1 }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const download = (item, size) => {
    window.open(`${BACKEND}/api/media/item/${item.id}/file?size=${size}&download=1`, "_blank");
  };

  const downloadCustom = (item) => {
    const w = parseInt(customWidth, 10);
    if (!w || w < 50 || w > 4000) {
      setError("Breite muss zwischen 50 und 4000 Pixel liegen");
      return;
    }
    setError("");
    window.open(`${BACKEND}/api/media/item/${item.id}/file?width=${w}&download=1`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-media-tab">
      <div className="mb-6">
        <h3 className="font-heading text-xl text-white flex items-center gap-2">
          <Images className="w-5 h-5 text-[#C5993A]" /> Medien-Bibliothek
        </h3>
        <p className="text-xs text-[#3A4F60] mt-1">
          Bilder für die Website verwalten. Bei mehreren Hero-Bildern wird beim Seitenaufruf zufällig eines geladen.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        {/* Slot sidebar */}
        <nav data-testid="media-slot-nav" className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-2 h-fit">
          {slots.map((s) => {
            const active = s.key === activeSlot;
            return (
              <button
                key={s.key}
                data-testid={`slot-tab-${s.key}`}
                onClick={() => setActiveSlot(s.key)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-sm text-left transition-colors ${
                  active ? "bg-[#C5993A]/20 text-white" : "text-[#8899AA] hover:bg-[#0C1620] hover:text-white"
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <ImageIcon className={`w-3.5 h-3.5 flex-shrink-0 ${active ? "text-[#C5993A]" : "text-[#3A4F60]"}`} />
                  <span className="truncate">{s.label}</span>
                </span>
                <span className="text-[10px] font-mono bg-[#0C1620] text-[#8899AA] px-1.5 py-0.5 rounded flex-shrink-0">
                  {s.items.length}
                  {s.multi && "+"}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Slot content */}
        <div data-testid="media-slot-content">
          {!slot ? null : (
            <>
              <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#C5993A]" /> {slot.label}
                </h4>
                <p className="text-xs text-[#8899AA] mt-1">
                  {slot.multi
                    ? "Mehrere Bilder möglich — eines wird zufällig pro Seitenaufruf geladen."
                    : "Ein Bild — neue Uploads ersetzen das vorhandene."}
                </p>
              </div>

              {/* Items */}
              {slot.items.length === 0 ? (
                slot.default_url ? (
                  <div className="bg-[#0C1620] border border-dashed border-[#3A4F60]/40 rounded-lg p-4 mb-4" data-testid={`media-default-${slot.slot}`}>
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                      <div className="relative aspect-[16/10] w-full sm:w-64 bg-[#0C1620] border border-[#3A4F60]/30 rounded overflow-hidden flex-shrink-0">
                        <img src={slot.default_url} alt="Standardbild" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 space-y-2 min-w-0">
                        <div>
                          <p className="text-xs uppercase tracking-wider text-[#C5993A]">Standardbild aktiv</p>
                          <p className="text-[11px] text-[#8899AA] mt-1">Es wurde noch nichts hochgeladen — dies ist der Fallback, der auf der Webseite angezeigt wird.</p>
                          <p className="text-[10px] text-[#3A4F60] mt-1 truncate" title={slot.default_url}>{slot.default_url}</p>
                        </div>
                        <a
                          href={slot.default_url}
                          download
                          data-testid={`download-default-${slot.slot}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[#C5993A]/40 text-[#C5993A] text-xs hover:bg-[#C5993A]/10 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" /> Standardbild herunterladen
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#0C1620] border border-dashed border-[#3A4F60]/40 rounded-lg p-8 mb-4 text-center text-[#8899AA] text-sm">
                    Noch kein Bild hochgeladen.
                  </div>
                )
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4" data-testid="media-items-grid">
                  {slot.items.map((item) => {
                    const expanded = activeDownloadItem === item.id;
                    const hidden = item.is_visible === 0;
                    return (
                      <div
                        key={item.id}
                        data-testid={`media-item-${item.id}`}
                        className={`bg-[#12202f] border rounded-lg overflow-hidden transition-all ${hidden ? "border-amber-500/30 opacity-60" : "border-[#3A4F60]/30"}`}
                      >
                        <div className="relative aspect-[16/10] bg-[#0C1620]">
                          <img src={`${BACKEND}${item.thumb_url}`} alt={item.label || "Bild"} className={`w-full h-full object-cover transition-all ${hidden ? "grayscale" : ""}`} />
                          {hidden && (
                            <div className="absolute inset-0 bg-[#0C1620]/40 flex items-center justify-center pointer-events-none">
                              <span className="px-2.5 py-1 rounded-md bg-amber-500/90 text-[#0C1620] text-[10px] font-medium uppercase tracking-wider">
                                Versteckt
                              </span>
                            </div>
                          )}
                          <div className="absolute top-2 right-2 flex gap-1.5">
                            <button
                              data-testid={`toggle-visible-${item.id}`}
                              onClick={() => toggleVisible(item)}
                              className={`w-7 h-7 rounded-full bg-[#0C1620]/80 border flex items-center justify-center transition-colors ${
                                hidden
                                  ? "border-amber-400/50 text-amber-300 hover:bg-amber-400 hover:text-[#0C1620]"
                                  : "border-[#C5993A]/40 text-[#C5993A] hover:bg-[#C5993A] hover:text-[#0C1620]"
                              }`}
                              title={hidden ? "Anzeigen" : "Verstecken"}
                            >
                              {hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              data-testid={`delete-item-${item.id}`}
                              onClick={() => removeItem(item)}
                              className="w-7 h-7 rounded-full bg-[#0C1620]/80 border border-red-400/40 text-red-400 hover:bg-red-400 hover:text-white flex items-center justify-center"
                              title="Löschen"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="p-3 space-y-2">
                          <div className="grid grid-cols-3 gap-x-2 text-[11px] text-[#8899AA]">
                            <div><span className="text-[#3A4F60]">Format:</span> <span className="text-white">{item.ext.toUpperCase()}</span></div>
                            <div><span className="text-[#3A4F60]">Größe:</span> <span className="text-white">{item.width}×{item.height}</span></div>
                            <div><span className="text-[#3A4F60]">Datei:</span> <span className="text-white">{fmtBytes(item.size_bytes)}</span></div>
                          </div>
                          <Button
                            data-testid={`expand-download-${item.id}`}
                            onClick={() => setActiveDownloadItem(expanded ? null : item.id)}
                            variant="ghost"
                            size="sm"
                            className="w-full text-[#C5993A] hover:bg-[#C5993A]/10 border border-[#C5993A]/30 h-8"
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" /> {expanded ? "Schließen" : "Download"}
                          </Button>
                          {expanded && (
                            <div className="space-y-2 pt-2 border-t border-[#3A4F60]/30">
                              <div className="grid grid-cols-4 gap-1">
                                {PRESETS.map((p) => (
                                  <Button
                                    key={p.key}
                                    data-testid={`download-${item.id}-${p.key}`}
                                    onClick={() => download(item, p.key)}
                                    variant="ghost"
                                    size="sm"
                                    className="bg-[#0C1620] border border-[#3A4F60]/40 hover:border-[#C5993A]/60 text-[11px] h-7 px-1"
                                  >
                                    {p.label}
                                  </Button>
                                ))}
                              </div>
                              <div className="flex gap-1">
                                <Input
                                  data-testid={`custom-width-${item.id}`}
                                  type="number"
                                  min={50}
                                  max={4000}
                                  value={customWidth}
                                  onChange={(e) => setCustomWidth(e.target.value)}
                                  placeholder="Breite px"
                                  className="bg-[#0C1620] border-[#3A4F60]/50 text-white h-7 text-xs"
                                />
                                <Button
                                  data-testid={`download-custom-${item.id}`}
                                  onClick={() => downloadCustom(item)}
                                  size="sm"
                                  className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 h-7 px-3 text-xs"
                                >
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Upload */}
              <div className="bg-[#12202f] border border-[#3A4F60]/30 rounded-lg p-5">
                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#C5993A]" /> {slot.multi ? "Weiteres Bild hinzufügen" : "Bild ersetzen / hochladen"}
                </h4>
                <div
                  data-testid="upload-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  className="border-2 border-dashed border-[#3A4F60]/50 hover:border-[#C5993A]/60 rounded-lg p-6 text-center cursor-pointer transition-colors"
                >
                  <Upload className="w-7 h-7 text-[#3A4F60] mx-auto mb-2" />
                  <p className="text-sm text-white">{uploading ? "Lädt hoch..." : "Datei wählen oder hierher ziehen"}</p>
                  <p className="text-xs text-[#3A4F60] mt-1">JPG / PNG / WEBP · max. 10 MB</p>
                  <input
                    ref={fileInputRef}
                    data-testid="upload-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onFileChange}
                    className="hidden"
                    disabled={uploading}
                  />
                </div>
              </div>

              {(error || success) && (
                <div className="mt-4">
                  {error && (
                    <p data-testid="media-error" className="text-red-400 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> {error}
                    </p>
                  )}
                  {success && (
                    <p data-testid="media-success" className="text-green-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" /> {success}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
